import { memo, useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import {
    CategoryScale,
    Chart,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
} from 'chart.js';

import {
    GREEN_COLOR,
    GREEN_TOP_FILL_COLOR,
    RED_BOTTOM_FILL_COLOR,
    RED_COLOR,
} from '../combined-bar-line-chart/helpers/color-utils';

import cls from './sparkline.module.css';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

type SparklineColor = 'positive' | 'negative' | 'neutral' | 'auto';

interface SparklineProps {
    className?: string;
    color?: SparklineColor;
    data?: number[];
    isLoading?: boolean;
}

const NEUTRAL_COLOR = '#1F4ECD';
const NEUTRAL_FILL_COLOR = 'rgba(31, 78, 205, 0.15)';

function getSparklineColors(
    color: SparklineColor,
    data: number[],
): { fillColor: string; lineColor: string } {
    if (color === 'positive') {
        return { fillColor: GREEN_TOP_FILL_COLOR, lineColor: GREEN_COLOR };
    }

    if (color === 'negative') {
        return { fillColor: RED_BOTTOM_FILL_COLOR, lineColor: RED_COLOR };
    }

    if (color === 'neutral') {
        return { fillColor: NEUTRAL_FILL_COLOR, lineColor: NEUTRAL_COLOR };
    }

    // Auto: determine based on last value vs first value
    const lastValue = data[data.length - 1] ?? 0;
    const firstValue = data[0] ?? 0;
    const isPositive = lastValue >= firstValue;

    return isPositive
        ? { fillColor: GREEN_TOP_FILL_COLOR, lineColor: GREEN_COLOR }
        : { fillColor: RED_BOTTOM_FILL_COLOR, lineColor: RED_COLOR };
}

const SKELETON_DATA_POINTS = 10;
const SKELETON_DATA = Array.from({ length: SKELETON_DATA_POINTS }, () => 0);

export const Sparkline = memo((props: SparklineProps) => {
    const { className, color = 'auto', data, isLoading = false } = props;

    // Use skeleton data (flat line at 0) when loading, otherwise use real data
    const displayData = useMemo(() => {
        if (isLoading || !data || data.length < 2) {
            return SKELETON_DATA;
        }

        return data;
    }, [data, isLoading]);

    // Use neutral color when loading, otherwise determine from color prop
    const { fillColor, lineColor } = useMemo(() => {
        if (isLoading) {
            return { fillColor: NEUTRAL_FILL_COLOR, lineColor: NEUTRAL_COLOR };
        }

        return getSparklineColors(color, displayData);
    }, [color, displayData, isLoading]);

    const chartData = useMemo(
        () => ({
            datasets: [
                {
                    backgroundColor: fillColor,
                    borderColor: lineColor,
                    borderWidth: 1.5,
                    data: displayData,
                    fill: 'start',
                    pointRadius: 0,
                    tension: 0.3,
                },
            ],
            labels: displayData.map((_, index) => index.toString()),
        }),
        [displayData, fillColor, lineColor],
    );

    const options = useMemo(
        () => ({
            animation: {
                duration: isLoading ? 0 : 750,
                easing: 'easeOutQuart' as const,
            },
            layout: {
                padding: 0,
            },
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
            },
            responsive: true,
            scales: {
                x: {
                    display: false,
                    grid: { display: false },
                },
                y: {
                    display: false,
                    grid: { display: false },
                },
            },
        }),
        [isLoading],
    );

    // Always render the chart (skeleton when loading, real data otherwise)
    const shouldRender = isLoading || (data && data.length >= 2);

    if (!shouldRender) {
        return null;
    }

    return (
        <div className={className ? `${cls.root} ${className}` : cls.root}>
            <Line
                data={chartData}
                options={options}
            />
        </div>
    );
});

Sparkline.displayName = 'Sparkline';
